'use strict';

/* This will be used for rendering virtual DOM: https://v2.vuejs.org/v2/guide/render-function.html#The-Virtual-DOM 
      
  schema Field:
  
  { 
    name: String                  * Fields use name to bind their value and 
    type: String                  * DEPRECATED: Provides compatibility with old 'TYPE_COMPONENT_MAP' map.
    
    as?: String                   * Override default field options based on other filter. (e.g. { name: "with_user_name", as: "with_name" } )
    
    formatter?: Function=>String, * Provide custom formatting function for displaying the entered value in FiltersChips
    component? Component,         * render createElement() first argument, html tag string or (async) component import

    label?: String,               * Provide another translation key, or a raw text override for field name.
    placeholder?: String,         * Describes example of a valid field value, falls back on label.
    rules?: Array                 * List of validations, as available in this.$validations
  }   

*/

import { format, parseISO } from 'date-fns';
import { i18n } from '@/plugins/vue-i18n';

const fieldTypes = {
  type_text: {
    component: () => import('@/components/forms/inputs/Textfield.vue'),
    hideDetails: 'auto',
    prependInnerIcon: "mdi-form-textbox",
  },
  type_status_select: {
    component: () => import('@/components/forms/inputs/StatusSelect.vue'),
    formatter(v) {
      return i18n.t(`statusses.${v.value || v}`);
    },
    // requires items (array of statusses). e.g items: ['pending', 'approved', 'rejected'],
  },
  type_date: {
    component: () => import('@/components/forms/inputs/DateInput.vue'),
    formatter: v => (v == undefined ? v : format(parseISO(v), 'dd-MM-yyyy')),
    icon: "calendar.svg",
  },
  type_select: {
    component: () => import('@/components/forms/inputs/Select.vue'),
    formatter(v) {
      return this.items?.find(x => x.value === v).text;
    },
    items: [],
    hideDetails: 'auto',
  },
  with_user: {
    component: () => import('@/components/select/EmployeeSelect.vue'),
    // requires indexServiceAction, initialServiceAction
  },
};

export default Object.freeze({
  ...fieldTypes,
  //TEXT
  with_name: {
    ...fieldTypes.type_text,
  },
  with_code: {
    ...fieldTypes.type_text,
  },
  with_description: {
    ...fieldTypes.type_text,
  },
  //STATUS
  with_status: {
    ...fieldTypes.type_status_select,
  },
  //DATE
  with_date: {
    ...fieldTypes.type_date,
  },
  from_date: {
    ...fieldTypes.type_date,
  },
  till_date: {
    ...fieldTypes.type_date,
  },
  with_created_at: {
    ...fieldTypes.type_date,
  },
  with_created_at_from: {
    ...fieldTypes.type_date,
  },
  with_created_at_till: {
    ...fieldTypes.type_date,
  },
  scoped_hidden_records: {
    component: () => import('@/components/forms/inputs/ScopedHiddenRecordsSelect.vue'),
  },
  //SearchInput variants/helpers
  with_manager: {
    ...fieldTypes.with_user,
  },
  with_project: {
    component: () => import('@/components/select/ProjectSelect.vue'),
    // requires indexServiceAction, initialServiceAction
  },
  with_account: {
    component: () => import('@/components/select/AccountSelect.vue'),
    // requires indexServiceAction, initialServiceAction
  },
  with_job: {
    component: () => import('@/components/select/JobSelect.vue'),
    // requires indexServiceAction, initialServiceAction
  },
  with_phase: {
    component: () => import('@/components/select/PhaseSelect.vue'),
    smallAvatarSize: true,
    // requires indexServiceAction, initialServiceAction
  },
  type_autocomplete: {
    component: () => import('vuetify/lib/components/VAutocomplete'),
  },
  type_select: {
    component: () => import('@/components/forms/inputs/Select.vue'),
    formatter(v) {
      return this.items?.find(x => x.value === v).text;
    },
    items: [],
    hideDetails: 'auto',
  },
  type_query_select: {
    component: () => import('@/components/forms/inputs/QuerySelect.vue'),
    hideDetails: 'auto',
    // requires indexServiceAction
  },
  type_textarea: {
    component: () => import('@/components/forms/inputs/Textarea.vue'),
    hideDetails: 'auto',
  },
  // custom inputs
  type_time: {
    component: () => import('@/components/forms/inputs/TimeInput.vue'),
  },
  type_tristate: {
    component: () =>
      import('@/components/forms/inputs/TriStateBooleanInput.vue'),
    formatter(v) {
      let { trueText, falseText } = this;
      if (v === null)
        return v;

      // Check for both boolean false and string "false"
      const isFalse = v === false || v === "false";
      return i18n.t(isFalse ? falseText : trueText);
    },
    trueText: 'general.yes', // same defaults as component
    falseText: 'general.no',
  },
  type_unit_quantity: {
    component: () => import('@/components/forms/inputs/UnitQuantityInput.vue'),
  },
  type_number: {
    component: () => import('@/components/forms/inputs/NumberInput.vue'),
  },
  type_search_input: {
    component: () => import('@/components/forms/inputs/SearchInput.vue'),
    // requires indexServiceAction, initialServiceAction
  },
});
